// ═══════════════════════════════════════════════════════════════════════════
// ¿Quién me debe? — la cartera por cliente, del que más debe al que menos
//
// El operador: "Para las vinícolas falta una vista por clientes. ¿Qué clientes
// tienen facturas o remisiones pendientes de pago?"
//
// Cuentas agrupaba por cliente pero ordenaba por la última venta y ponía al
// frente el total histórico; para saber quién debe había que escoger un filtro
// y leer tarjeta por tarjeta. Esto contesta la pregunta de golpe: solo los que
// deben, el que más debe primero, con la deuda partida en facturas y
// remisiones sin facturar, y cuántos días lleva sin cobrarse lo más viejo.
//
// Los números salen de `carteraPorCliente` (dominio, probado); aquí solo se
// dibujan. Cada documento se puede cobrar desde aquí mismo con el mismo
// diálogo de pagos que usan Historial y Cuentas.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { fmt } from "../pdf/comunes.js";
import { Card } from "./basicos.jsx";
import { carteraPorCliente, resumenCarteraPorCliente } from "../dominio/cobranza.js";
import { esperaFolio } from "../datos/remisiones-pg.js";

const fecha = (iso) => { try { return new Date(iso).toLocaleDateString("es-MX"); } catch { return ""; } };

/** "214 días" en rojo pasados los 90, ámbar pasados los 30. Es aviso, no veredicto. */
const colorDias = (d) => d >= 90 ? "#f87171" : d >= 30 ? "#facc15" : "#888";

const Chip = ({ color, bg, children, title }) => (
  <span title={title} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4,
                               background: bg, color, whiteSpace: "nowrap" }}>
    {children}
  </span>
);

export function QuienMeDebe({ clients, invoices, abrirPagos, onVerCliente }) {
  const [abierto, setAbierto] = useState(null);
  const [verTodos, setVerTodos] = useState(false);

  const cartera = useMemo(() => carteraPorCliente(clients, invoices), [clients, invoices]);
  const resumen = useMemo(() => resumenCarteraPorCliente(cartera), [cartera]);
  const porId = useMemo(() => new Map((invoices || []).map(i => [i.id, i])), [invoices]);

  const LIMITE = 8;
  const visibles = verTodos ? cartera : cartera.slice(0, LIMITE);

  return (
    <Card style={{ marginBottom: 16, border: "1px solid #2a1a1a" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 10, color: "#c97070", letterSpacing: 2 }}>💸 ¿QUIÉN ME DEBE?</div>
          <div style={{ fontSize: 11, color: "#666", marginTop: 3 }}>
            {resumen.clientes === 0
              ? "Nadie. No hay facturas ni remisiones con saldo por cobrar."
              : <>
                  {resumen.clientes} cliente{resumen.clientes !== 1 ? "s" : ""} ·{" "}
                  {resumen.facturas.n} factura{resumen.facturas.n !== 1 ? "s" : ""} ({fmt(resumen.facturas.saldo)}) ·{" "}
                  {resumen.remisiones.n} remisi{resumen.remisiones.n !== 1 ? "ones" : "ón"} sin facturar ({fmt(resumen.remisiones.saldo)})
                  {resumen.repPendientes > 0 && <span style={{ color: "#c9a96e" }}> · ⚠ {resumen.repPendientes} sin complemento</span>}
                </>}
          </div>
        </div>
        {resumen.clientes > 0 && (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#c9a96e" }}>{fmt(resumen.saldo)}</div>
            <div style={{ fontSize: 10, color: "#666" }}>por cobrar</div>
          </div>
        )}
      </div>

      {visibles.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 4 }}>
          {visibles.map(c => {
            const isOpen = abierto === c.cliente.id;
            return (
              <div key={c.cliente.id}
                style={{ background: isOpen ? "#100c0c" : "#0a0a0a", borderRadius: 6,
                         border: "1px solid " + (isOpen ? "#3a1a1a" : "#161616") }}>
                {/* El renglón: nombre, cuánto es factura y cuánto no, antigüedad, saldo */}
                <div onClick={() => setAbierto(isOpen ? null : c.cliente.id)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                           cursor: "pointer", flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#e8e0d4",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.cliente.name}
                      {!c.cliente.enCatalogo && <span title="Ya no está en el catálogo de clientes"
                                                      style={{ color: "#666", fontSize: 10 }}> · fuera del catálogo</span>}
                    </div>
                    <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                      {c.facturas.n > 0 && (
                        <Chip color="#4ade80" bg="#0d1f0d" title="Con CFDI vigente">
                          {c.facturas.n} factura{c.facturas.n !== 1 ? "s" : ""} · {fmt(c.facturas.saldo)}
                        </Chip>
                      )}
                      {c.remisiones.n > 0 && (
                        <Chip color="#e8d9a0" bg="#1f1a0d" title="Remisiones sin factura">
                          {c.remisiones.n} remisi{c.remisiones.n !== 1 ? "ones" : "ón"} · {fmt(c.remisiones.saldo)}
                        </Chip>
                      )}
                      {c.repPendientes > 0 && (
                        <Chip color="#c9a96e" bg="#1a1408" title="Facturas PPD con pagos sin complemento">
                          ⚠ {c.repPendientes} sin REP
                        </Chip>
                      )}
                      {c.aFavor > 0 && (
                        <Chip color="#70c9c9" bg="#0d1a1a" title="Dinero suyo sin aplicar; no se resta del saldo">
                          a favor {fmt(c.aFavor)}
                        </Chip>
                      )}
                    </div>
                  </div>
                  <div title={`El documento más antiguo sin cobrar es del ${fecha(c.masAntigua)}`}
                       style={{ fontSize: 11, color: colorDias(c.diasMasAntigua), whiteSpace: "nowrap" }}>
                    {c.diasMasAntigua} día{c.diasMasAntigua !== 1 ? "s" : ""}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#c9a96e", minWidth: 96,
                                textAlign: "right", whiteSpace: "nowrap" }}>
                    {fmt(c.saldo)}
                  </div>
                </div>

                {/* Abierto: sus documentos, del más viejo al más nuevo, cobrables aquí */}
                {isOpen && (
                  <div style={{ padding: "0 10px 10px", borderTop: "1px solid #1a1a1a" }}>
                    {c.documentos.map(d => {
                      const inv = porId.get(d.id);
                      return (
                        <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8,
                                                 padding: "6px 0", fontSize: 12, flexWrap: "wrap",
                                                 borderBottom: "1px solid #131313" }}>
                          <span style={{ fontWeight: 600, color: "#e8e0d4", minWidth: 52 }}>
                            {esperaFolio(inv || d) ? "s/folio" : `#${d.folio}`}
                          </span>
                          <span style={{ color: "#888", minWidth: 70 }}>{fecha(d.fecha)}</span>
                          <span style={{ color: colorDias(d.dias), fontSize: 11, minWidth: 50 }}>{d.dias} d</span>
                          <span style={{ fontSize: 10, color: d.facturada ? "#4ade80" : "#e8d9a0" }}>
                            {d.facturada ? "factura" : "remisión"}{d.requiereREP ? " ⚠ REP" : ""}
                          </span>
                          <span style={{ marginLeft: "auto", color: "#888", fontSize: 11, whiteSpace: "nowrap" }}>
                            {d.pagado > 0 ? `${fmt(d.pagado)} de ${fmt(d.total)} · falta ` : ""}
                            <span style={{ color: "#c9a96e", fontWeight: 600 }}>{fmt(d.saldo)}</span>
                          </span>
                          {inv && abrirPagos && (
                            <button onClick={() => abrirPagos(inv)}
                              style={{ background: "#160f0f", border: "1px solid #3a1a1a", borderRadius: 4,
                                       color: "#e8b0b0", cursor: "pointer", padding: "2px 8px", fontSize: 11 }}>
                              Registrar pago
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {onVerCliente && (
                      <button onClick={() => onVerCliente(c.cliente.id)}
                        style={{ marginTop: 8, background: "none", border: "1px solid #222", borderRadius: 4,
                                 color: "#888", cursor: "pointer", padding: "3px 10px", fontSize: 11 }}>
                        Ver toda la cuenta ↓
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {cartera.length > LIMITE && (
            <button onClick={() => setVerTodos(v => !v)}
              style={{ alignSelf: "center", marginTop: 4, background: "none", border: "none",
                       color: "#888", cursor: "pointer", fontSize: 11 }}>
              {verTodos ? "Ver menos" : `Ver los ${cartera.length - LIMITE} restantes`}
            </button>
          )}
        </div>
      )}
    </Card>
  );
}
